import { ElevationCalculator, BatchCalculator } from './calculator';
import { TileManager } from './tile';
import { createLogger, Logger } from './utils';
import type {
    Coordinates,
    CoordinatesElevation,
    ElevationProviderConfig,
    Attribution,
    SetElevationsOptions,
    GetElevationsAlongOptions,
    GetElevationOptions,
} from './types';

const logger: Logger = createLogger('ElevationProvider');

/**
 * Main API class for retrieving elevation data from geographic coordinates
 */
export class ElevationProvider {
    private readonly config: Required<ElevationProviderConfig>;
    private readonly tileManager: TileManager;
    private readonly calculator: ElevationCalculator;
    private readonly batchCalculator: BatchCalculator;

    // ============================================================================
    // CONSTRUCTOR & CONFIGURATION
    // ============================================================================

    constructor(config: ElevationProviderConfig = {}) {
        this.config = {
            zoomLevel: config.zoomLevel ?? 12,
            cacheSize: config.cacheSize ?? 100,
            tileUrlTemplate:
                config.tileUrlTemplate ?? 'https://tiles.mapterhorn.com/{z}/{x}/{y}.webp',
            tileSize: config.tileSize ?? 512,
            attribution: config.attribution ?? {
                text: 'Mapterhorn elevation data. See mapterhorn.com/attribution/ for details.',
                url: 'https://mapterhorn.com/attribution/',
            },
        };
        logger.dir('Config :', this.config);

        this.validateConfig();
        this.tileManager = new TileManager(this.config.tileUrlTemplate, this.config.cacheSize);
        this.calculator = new ElevationCalculator(this.tileManager, this.config.tileSize);
        this.batchCalculator = new BatchCalculator(this.calculator);
    }

    /**
     * Get current configuration
     */
    public getConfig(): Readonly<Required<ElevationProviderConfig>> {
        return { ...this.config };
    }

    /**
     * Get attribution information for elevation data
     */
    public getAttribution(): Attribution {
        return this.config.attribution;
    }

    // ============================================================================
    // PUBLIC API - SINGLE COORDINATE METHODS
    // ============================================================================

    /**
     * Get elevation at specific coordinates
     * @param latitude - Latitude in decimal degrees
     * @param longitude - Longitude in decimal degrees
     * @param options - Optional parameters
     */
    public async getElevation(
        latitude: number,
        longitude: number,
        options?: GetElevationOptions
    ): Promise<number> {
        await this.tileManager.initCache();
        const interpolation = options?.interpolation ?? true;
        const coords: Coordinates = { latitude, longitude };
        return await this.calculator.getElevation(coords, this.config.zoomLevel, interpolation);
    }

    // ============================================================================
    // PUBLIC API - BULK COORDINATE METHODS
    // ============================================================================

    /**
     * Get elevations for multiple coordinates from an interable
     * @param coordinates - Iteratable of coordinates
     * @param options - Optional parameters
     */
    public async setElevations(
        coordinates: Iterable<Coordinates>,
        options?: SetElevationsOptions
    ): Promise<void> {
        await this.tileManager.initCache();
        const interpolation = options?.interpolation ?? true;
        await this.batchCalculator.setElevations(coordinates, this.config.zoomLevel, interpolation);
    }

    /**
     * Get elevations along a path defined by multiple coordinates
     * @param path - Array of coordinates defining the path
     * @param options - Optional parameters
     */
    public async getElevationsAlong(
        path: Coordinates[],
        options?: GetElevationsAlongOptions
    ): Promise<CoordinatesElevation[]> {
        await this.tileManager.initCache();
        const step = options?.step ?? 10;
        const minDistance = options?.minDistance ?? 1;
        const interpolation = options?.interpolation ?? true;
        const smoothingOptions = options?.smoothingOptions;
        const filterOptions = options?.filterOptions;
        return this.batchCalculator.getElevationsAlong(
            path,
            this.config.zoomLevel,
            step,
            minDistance,
            interpolation,
            smoothingOptions,
            filterOptions
        );
    }

    // ============================================================================
    // PRIVATE - VALIDATION
    // ============================================================================

    private validateConfig(): void {
        const { zoomLevel, cacheSize } = this.config;

        if (!Number.isInteger(zoomLevel) || zoomLevel < 0 || zoomLevel > 15) {
            throw new Error(
                `Invalid zoom level: ${zoomLevel}. Must be an integer between 0 and 15`
            );
        }

        if (!Number.isInteger(cacheSize) || cacheSize <= 0) {
            throw new Error(`Invalid cache size: ${cacheSize}. Must be a positive integer`);
        }

        const { tileSize } = this.config;
        if (!Number.isInteger(tileSize) || tileSize <= 0 || (tileSize & (tileSize - 1)) !== 0) {
            throw new Error(`Invalid tile size: ${tileSize}. Must be a positive power of 2`);
        }
    }
}
